import Input from "components/materials/Form/InputElement";
import classes from "./classes.module.scss";
import ProjectConfigResponseResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigResponseResource";
import Form, { useForm } from "components/materials/Form";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useCallback, useEffect, useState } from "react";
import ConfigService from "services/projects/ConfigService";
import { container } from "tsyringe";
import { useProjectId } from "hooks/useProjectId";
import assert from "assert";
import ProjectUpdateSocialsRequestResource from "common/resources/Project/ProjectUpdateSocialsRequestResource";
import ProjectService from "services/ProjectService";
import ProjectConfigRequestResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigRequestResource";
import { EToastType, useToast } from "contexts/Toast";

const projectConfigService = container.resolve(ConfigService);
const projectService = container.resolve(ProjectService);

type FormData = {
	[key: string]: unknown;
};

export default function SocialMedia() {
	const { showToast } = useToast();
	const { projectId } = useProjectId();
	const [isLoading, setIsLoading] = useState(false);
	const [hasDataChanged, setHasDataChanged] = useState(false);
	const [projectConfig, setProjectConfig] = useState<ProjectConfigResponseResource | null>(null);
	const { formRef, errors, handleError, clearErrors } = useForm();

	const onDataChange = useCallback(() => {
		if (hasDataChanged) return;
		setHasDataChanged(true);
	}, [hasDataChanged]);

	const validateProjectConfigData = useCallback(
		(formData: FormData) => {
			assert(projectConfig, "No project config");

			const projectConfigResource = ProjectConfigRequestResource.hydrate<ProjectConfigRequestResource>({
				title: projectConfig.title,
				subtitle: projectConfig.subtitle ?? undefined,
				description: projectConfig.description,
				developmentStage: projectConfig.developmentStage ?? undefined,
				projectNeeds: projectConfig.projectNeeds ?? undefined,
				contactEmail: formData["contactEmail"] ? (formData["contactEmail"] as string) : undefined,
			});

			return projectConfigResource.validateOrReject();
		},
		[projectConfig],
	);

	const validateProjectSocialsData = useCallback(
		(formData: FormData) => {
			assert(projectConfig, "No project config");

			const projectSocialsResource = ProjectUpdateSocialsRequestResource.hydrate<ProjectUpdateSocialsRequestResource>({
				discord: formData["discord"] ? (formData["discord"] as string) : undefined,
				instagram: formData["instagram"] ? (formData["instagram"] as string) : undefined,
				x: formData["x"] ? (formData["x"] as string) : undefined,
				linkedin: formData["linkedin"] ? (formData["linkedin"] as string) : undefined,
			});

			return projectSocialsResource.validateOrReject();
		},
		[projectConfig],
	);

	const onSubmit = useCallback(
		async (formData: FormData) => {
			try {
				assert(projectConfig, "No project config");
				setIsLoading(true);
				setHasDataChanged(false);
				clearErrors();
				const projectSocialsData = await validateProjectSocialsData(formData);
				const projectConfigData = await validateProjectConfigData(formData);
				await projectService.updateProjectSocials(projectId, projectSocialsData);
				await projectConfigService.update(projectId, projectConfig.id, projectConfigData);
				showToast({ message: I18n.asset.pages.professional_space.tabs.information.saved, type: EToastType.SUCCESS });
				setIsLoading(false);
			} catch (error) {
				handleError(error);
				setIsLoading(false);
			}
		},
		[clearErrors, handleError, projectId, validateProjectSocialsData, validateProjectConfigData, projectConfig, showToast],
	);

	useEffect(() => {
		projectConfigService.getByProjectId(projectId).then(setProjectConfig).catch(console.error);
	}, [projectId]);

	return (
		<Form className={classes["root"]} formRef={formRef} onChange={onDataChange} onSubmit={onSubmit} errors={errors}>
			<div className={classes["header-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.information.sub_tabs.contact_and_socials}
				</Typography>
				<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading} disabled={!hasDataChanged}>
					{I18n.asset.pages.professional_space.tabs.information.save}
				</Button>
			</div>

			<div className={classes["form"]}>
				<Input
					name="contactEmail"
					defaultValue={projectConfig?.contactEmail}
					label={I18n.asset.pages.professional_space.tabs.information.sub_tabs.contact_button}
					placeholder="johndoe@gmail.com"
				/>
				<Input name="discord" defaultValue={projectConfig?.project.discord} label="Discord" placeholder="https://discord.gg/" />
				<Input name="instagram" defaultValue={projectConfig?.project.instagram} label="Instagram" placeholder="https://instagram.com/" />
				<Input name="x" label="X" defaultValue={projectConfig?.project.x} placeholder="https://x.com/" />
				<Input name="linkedin" defaultValue={projectConfig?.project.linkedin} label="Linkedin" placeholder="https://linkedin.com/in/" />
			</div>
		</Form>
	);
}
